import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '../components/page-action-bar/page-action-bar';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '../components/product-image-editor.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  ICategory,
  IProduct,
  IModifierGroup,
  IModifier,
  IRecipeLine,
  IRecipeIngredient,
  IVariantCell,
  IStockItem,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import { shopBrandStyles } from '../styles/shop-brand-styles';

const SLUG_RX = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// One row in the variant editor — maps 1:1 to a cell. `id` is the cell +
// dimension-value id (preserved on edit; derived from the label for new rows).
interface VariantValueRow {
  id: string;
  label: string;
  price: string;
  barcode: string;
}

// Combined create + edit page. /products/new for create, /products/{id} for
// edit. Variant-cell model (docs/phase-variant-cell-model.md): a product is
// either SIMPLE (one default cell with a price + recipe) or has VARIANTS
// (one dimension, e.g. Size, with a priced cell per value). Modifiers are
// sale-time decorations on either kind.
@customElement('product-edit-page')
export class MenuEditPage extends Base {
  @state() private _loading = true;
  @state() private _busy = false;
  @state() private _saved = false;
  @state() private _error: string | null = null;
  @state() private _existing: IProduct | null = null;

  @state() private _categories: ICategory[] = [];
  @state() private _allStock: IStockItem[] = [];
  @state() private _allProducts: IProduct[] = [];

  // Basics.
  @state() private _id = '';
  @state() private _name = '';
  @state() private _description = '';
  @state() private _categoryId: string = '';
  @state() private _displayOrder = '0';
  @state() private _isActive = true;

  // Variants. When on, the product has one dimension and a priced cell per
  // value; when off, a single default cell carries the price + recipe.
  @state() private _hasVariants = false;
  @state() private _dimensionName = 'Size';
  @state() private _variantValues: VariantValueRow[] = [];

  // Simple-product (default cell) fields.
  @state() private _price = '';
  @state() private _barcode = '';
  @state() private _ingredients: IRecipeIngredient[] = [];
  @state() private _components: { productId: string; quantity: number }[] = [];
  @state() private _instructions = '';

  // Modifiers (both kinds).
  @state() private _optionGroups: IModifierGroup[] = [];

  static styles = [
    sharedStyles,
    shopBrandStyles,
    css`
      :host {
        display: block;
        max-width: 820px;
        margin: 0 auto;
        padding: clamp(12px, 3vw, 20px);
        box-sizing: border-box;
        font-family: var(--font-body, var(--sl-font-sans));
      }
      @media (max-width: 720px) {
        :host {
          padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));
        }
      }
      .page-head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 1rem;
        flex-wrap: wrap;
      }
      .page-head h1 {
        font-family: var(--font-display, var(--sl-font-sans));
        font-weight: 800;
        font-size: clamp(1.4rem, 4.5vw, 1.9rem);
        letter-spacing: -0.025em;
        margin: 0;
        flex: 1;
        min-width: 0;
        color: var(--s-ink, inherit);
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      sl-details {
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
        border-radius: 14px;
        background: var(--s-canvas, var(--sl-color-neutral-0));
        box-shadow: var(--s-shadow-soft, none);
      }
      sl-details::part(base) {
        border: none;
        background: transparent;
      }
      sl-details::part(header) {
        padding: 14px 16px;
        font-family: var(--font-body, var(--sl-font-sans));
      }
      sl-details::part(summary) {
        font-family: var(--font-display, var(--sl-font-sans));
        font-weight: 700;
        font-size: 1rem;
        letter-spacing: -0.01em;
        color: var(--s-ink, inherit);
      }
      sl-details::part(content) {
        padding: 0 16px 16px;
      }
      .summary-line {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .summary-line .count {
        font-size: 11px;
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 999px;
        background: var(--s-canvas-soft, var(--sl-color-neutral-100));
        color: var(--s-ink-soft, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
      }
      .section-hint {
        font-size: 0.82rem;
        color: var(--s-ink-soft, var(--sl-color-neutral-600));
        margin: 0 0 12px;
        line-height: 1.45;
      }
      .row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      @media (max-width: 560px) {
        .row {
          grid-template-columns: 1fr;
        }
      }
      .toggle-row {
        display: flex;
        gap: 10px;
        align-items: center;
      }
      .ingredient-row {
        display: grid;
        grid-template-columns: 1fr 130px 44px;
        gap: 8px;
        align-items: end;
        padding: 6px 0;
      }
      @media (max-width: 480px) {
        .ingredient-row {
          grid-template-columns: 1fr 96px 44px;
        }
      }
      /* Variant value row: label + price + barcode + trash. Stacks the
         barcode under label+price on phones. */
      .value-row {
        display: grid;
        grid-template-columns: 1fr 110px 150px 44px;
        gap: 8px;
        align-items: end;
        padding: 6px 0;
      }
      @media (max-width: 600px) {
        .value-row {
          grid-template-columns: 1fr 96px 44px;
        }
        .value-row > sl-input:nth-child(3) {
          grid-column: 1 / -1;
        }
      }
      .group-card {
        margin-top: 10px;
        padding: 12px;
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
        border-radius: 12px;
        background: var(--s-canvas-soft, var(--sl-color-neutral-50));
      }
      .option-row {
        display: grid;
        grid-template-columns: 1fr 1fr 110px 44px;
        gap: 8px;
        align-items: end;
        padding: 4px 0;
      }
      @media (max-width: 600px) {
        .option-row {
          grid-template-columns: 1fr 1fr;
        }
        .option-row sl-icon-button {
          justify-self: end;
          align-self: center;
        }
      }
      .option-row.option-row-quantifiable {
        grid-template-columns: 1fr 150px;
        padding-left: 12px;
        padding-bottom: 8px;
        border-bottom: 1px dashed var(--s-line, var(--sl-color-neutral-200));
      }
      @media (max-width: 600px) {
        .option-row.option-row-quantifiable {
          grid-template-columns: 1fr;
        }
      }
      .variant-empty {
        text-align: center;
        padding: 1.25rem;
        border: 1.5px dashed var(--s-line, var(--sl-color-neutral-300));
        border-radius: 12px;
        color: var(--s-ink-soft, var(--sl-color-neutral-600));
        font-size: 0.9rem;
      }
      .danger-zone {
        margin-top: 16px;
        padding: 14px;
        border: 1px dashed var(--s-bad, var(--sl-color-danger-300));
        border-radius: 12px;
      }
      .small-add {
        margin-top: 6px;
      }
      @media (max-width: 720px) {
        sl-button[size='small']::part(base) {
          min-height: 40px;
        }
        sl-icon-button::part(base) {
          padding: 8px;
        }
      }
    `,
  ];

  private _initStarted = false;

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;

    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }
    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can manage the menu.';
      this._loading = false;
      return;
    }

    const api = ApiFactory.authenticated();
    const [cats, allStock] = await Promise.all([
      api.listCategories(this.activeShop.shopName),
      api.listStock(this.activeShop.shopName),
    ]);
    if (cats.data) this._categories = cats.data.filter((c) => c.isActive);
    if (allStock.data) this._allStock = allStock.data.filter((s) => s.isActive);
    const allMenu = await api.listMenu(this.activeShop.shopName);
    if (allMenu.data)
      this._allProducts = allMenu.data.filter((m) => m.isActive);

    const editId = readEditIdFromPath();
    if (editId) {
      const r = await api.getProduct(this.activeShop.shopName, editId);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Menu item not found.';
        this._loading = false;
        return;
      }
      this._loadFrom(r.data);
    }
    this._loading = false;
  }

  // Map a loaded product onto the editor's flat form fields.
  private _loadFrom(m: IProduct) {
    this._existing = m;
    this._id = m.id;
    this._name = m.name;
    this._description = m.description ?? '';
    this._categoryId = m.categoryId ?? '';
    this._displayOrder = String(m.displayOrder);
    this._isActive = m.isActive;
    this._optionGroups = m.modifierGroups ?? [];

    const dims = m.variantDimensions ?? [];
    if (dims.length > 0) {
      this._hasVariants = true;
      const dim = dims[0];
      this._dimensionName = dim.name;
      this._variantValues = (m.cells ?? []).map((cell) => {
        const valueId = cell.dimensionValueIds[0] ?? '';
        const label =
          dim.values.find((v) => v.id === valueId)?.label ?? valueId;
        return {
          id: valueId,
          label,
          price: String(cell.price),
          barcode: cell.barcodes?.[0] ?? '',
        };
      });
    } else {
      this._hasVariants = false;
      const cell = m.cells?.[0];
      this._price = cell ? String(cell.price) : '';
      this._barcode = cell?.barcodes?.[0] ?? '';
      this._instructions = cell?.instructions ?? '';
      const recipe = cell?.recipe ?? [];
      this._ingredients = recipe
        .filter((l) => !!l.stockItemId)
        .map((l) => ({
          stockItemId: l.stockItemId as string,
          quantity: l.quantity,
        }));
      this._components = recipe
        .filter((l) => !!l.productId)
        .map((l) => ({
          productId: l.productId as string,
          quantity: l.quantity,
        }));
    }
  }

  private get _isEdit(): boolean {
    return this._existing !== null;
  }

  // ---- Simple-product recipe (ingredients = stock lines) ----
  private _addIngredient() {
    this._ingredients = [
      ...this._ingredients,
      { stockItemId: '', quantity: 0 },
    ];
    this._saved = false;
  }
  private _removeIngredient(i: number) {
    this._ingredients = this._ingredients.filter((_, j) => j !== i);
    this._saved = false;
  }
  private _updateIngredient(i: number, patch: Partial<IRecipeIngredient>) {
    this._ingredients = this._ingredients.map((x, j) =>
      j === i ? { ...x, ...patch } : x
    );
    this._saved = false;
  }

  // ---- Simple-product combo legs (product lines) ----
  private _addComponent() {
    this._components = [...this._components, { productId: '', quantity: 1 }];
    this._saved = false;
  }
  private _removeComponent(i: number) {
    this._components = this._components.filter((_, idx) => idx !== i);
    this._saved = false;
  }
  private _updateComponent(
    i: number,
    patch: Partial<{ productId: string; quantity: number }>
  ) {
    this._components = this._components.map((c, idx) =>
      idx === i ? { ...c, ...patch } : c
    );
    this._saved = false;
  }

  // ---- Variant values (each maps to a cell) ----
  private _addVariantValue() {
    this._variantValues = [
      ...this._variantValues,
      { id: '', label: '', price: '', barcode: '' },
    ];
    this._saved = false;
  }
  private _removeVariantValue(i: number) {
    this._variantValues = this._variantValues.filter((_, j) => j !== i);
    this._saved = false;
  }
  private _updateVariantValue(i: number, patch: Partial<VariantValueRow>) {
    this._variantValues = this._variantValues.map((v, j) =>
      j === i ? { ...v, ...patch } : v
    );
    this._saved = false;
  }

  // ---- Modifier groups (unchanged shape) ----
  private _addGroup() {
    this._optionGroups = [
      ...this._optionGroups,
      { id: '', name: '', required: false, multiSelect: false, modifiers: [] },
    ];
    this._saved = false;
  }
  private _removeGroup(i: number) {
    this._optionGroups = this._optionGroups.filter((_, j) => j !== i);
    this._saved = false;
  }
  private _updateGroup(i: number, patch: Partial<IModifierGroup>) {
    this._optionGroups = this._optionGroups.map((g, j) =>
      j === i ? { ...g, ...patch } : g
    );
    this._saved = false;
  }
  private _addOption(groupIdx: number) {
    this._optionGroups = this._optionGroups.map((g, j) =>
      j === groupIdx
        ? {
            ...g,
            modifiers: [
              ...g.modifiers,
              { id: '', name: '', priceDelta: 0, recipeAdditions: [] },
            ],
          }
        : g
    );
    this._saved = false;
  }
  private _removeOption(groupIdx: number, optIdx: number) {
    this._optionGroups = this._optionGroups.map((g, j) =>
      j === groupIdx
        ? { ...g, modifiers: g.modifiers.filter((_, k) => k !== optIdx) }
        : g
    );
    this._saved = false;
  }
  private _updateOption(
    groupIdx: number,
    optIdx: number,
    patch: Partial<IModifier>
  ) {
    this._optionGroups = this._optionGroups.map((g, j) =>
      j === groupIdx
        ? {
            ...g,
            modifiers: g.modifiers.map((o, k) =>
              k === optIdx ? { ...o, ...patch } : o
            ),
          }
        : g
    );
    this._saved = false;
  }

  // Build the cell list + dimensions from the form, validating as we go.
  // Returns null + sets _error on failure.
  private _buildCells(): {
    dimensions: IProduct['variantDimensions'];
    cells: IVariantCell[];
  } | null {
    if (this._hasVariants) {
      const dimName = this._dimensionName.trim();
      if (!dimName) {
        this._error =
          'Give the variant dimension a name (e.g. Size or Colour).';
        return null;
      }
      if (this._variantValues.length === 0) {
        this._error = 'Add at least one variant value.';
        return null;
      }
      const seen = new Set<string>();
      const cells: IVariantCell[] = [];
      const values: { id: string; label: string }[] = [];
      this._variantValues.forEach((row, idx) => {
        const label = row.label.trim();
        const id = (row.id || slugify(label)).trim().toLowerCase();
        const price = parseFloat(row.price);
        row.id = id; // preserve derived id back onto the row
        if (!label) {
          this._error = 'Each variant value needs a label.';
          return;
        }
        if (!id || !SLUG_RX.test(id)) {
          this._error = `Variant value "${label}" needs a valid code (auto-derived from the label — use simpler text).`;
          return;
        }
        if (seen.has(id)) {
          this._error = `Two variant values resolve to the same code "${id}". Use distinct labels.`;
          return;
        }
        if (isNaN(price) || price < 0) {
          this._error = `Variant value "${label}" needs a price ≥ 0.`;
          return;
        }
        seen.add(id);
        values.push({ id, label });
        const barcodes = row.barcode.trim() ? [row.barcode.trim()] : [];
        cells.push({
          id,
          dimensionValueIds: [id],
          price,
          barcodes,
          recipe: [],
          displayOrder: idx * 10,
        });
      });
      if (this._error) return null;
      const dimId = slugify(dimName) || 'variant';
      return { dimensions: [{ id: dimId, name: dimName, values }], cells };
    }

    // Simple product — one default cell.
    const price = parseFloat(this._price);
    if (isNaN(price) || price < 0) {
      this._error = 'Price must be a non-negative number.';
      return null;
    }
    for (const line of this._ingredients) {
      if (!line.stockItemId || line.quantity <= 0) {
        this._error = 'Each ingredient needs a stock item and a quantity > 0.';
        return null;
      }
    }
    for (const c of this._components) {
      if (!c.productId || c.quantity < 1) {
        this._error = 'Each combo item needs a product and a quantity ≥ 1.';
        return null;
      }
    }
    if (this._ingredients.length > 0 && this._components.length > 0) {
      this._error =
        'A product is either built from stock ingredients or composed of other products (a combo), not both.';
      return null;
    }
    const recipe: IRecipeLine[] = [
      ...this._ingredients.map((l) => ({
        stockItemId: l.stockItemId,
        quantity: l.quantity,
      })),
      ...this._components.map((c) => ({
        productId: c.productId,
        quantity: c.quantity,
      })),
    ];
    return {
      dimensions: [],
      cells: [
        {
          id: 'default',
          dimensionValueIds: [],
          price,
          barcodes: this._barcode.trim() ? [this._barcode.trim()] : [],
          recipe,
          instructions: this._instructions.trim() || null,
          displayOrder: 0,
        },
      ],
    };
  }

  private async _submit(e: Event) {
    e.preventDefault();
    if (this._busy || !this.activeShop) return;
    this._error = null;
    this._saved = false;

    const name = this._name.trim();
    if (!name) {
      this._error = 'Name is required.';
      return;
    }
    const built = this._buildCells();
    if (!built) return;
    const displayOrder = parseInt(this._displayOrder, 10) || 0;
    const description = this._description.trim() || null;

    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      if (this._isEdit) {
        const r = await api.updateProduct(this.activeShop.shopName, this._id, {
          name,
          description,
          categoryId: this._categoryId || null,
          variantDimensions: built.dimensions,
          cells: built.cells,
          modifierGroups: this._optionGroups,
          isActive: this._isActive,
          displayOrder,
        });
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Save failed.';
          return;
        }
        this._existing = r.data;
        this._saved = true;
      } else {
        const id = this._id.trim().toLowerCase();
        if (!id || !SLUG_RX.test(id)) {
          this._error =
            'Id must be 2-48 chars, lowercase letters/digits/hyphens.';
          return;
        }
        const r = await api.createProduct(this.activeShop.shopName, {
          id,
          name,
          description,
          categoryId: this._categoryId || null,
          variantDimensions: built.dimensions,
          cells: built.cells,
          modifierGroups: this._optionGroups,
          displayOrder,
        });
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Create failed.';
          return;
        }
        void router.navigate(resolveRouterPath('products'));
      }
    } finally {
      this._busy = false;
    }
  }

  private async _deactivate() {
    if (!this._existing || !this.activeShop) return;
    if (!confirm(`Deactivate "${this._existing.name}"?`)) return;
    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.deactivateProduct(
        this.activeShop.shopName,
        this._existing.id
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Deactivate failed.';
        return;
      }
      this._existing = r.data;
      this._isActive = r.data.isActive;
      this._saved = true;
    } finally {
      this._busy = false;
    }
  }

  private _back() {
    void router.navigate(resolveRouterPath('products'));
  }

  private _renderVariantValues() {
    return html`
      <p class="section-hint">
        Each value is a sellable, stockable variant — e.g. Small / Medium /
        Large, or one product per colour. Give each its own price.
      </p>
      <sl-input
        label="Dimension name"
        help-text="What the values vary by — Size, Colour, Pack."
        .value=${this._dimensionName}
        @sl-input=${(e: Event) => {
          this._dimensionName = (e.target as HTMLInputElement).value;
          this._saved = false;
        }}
      ></sl-input>
      <div style="margin-top: 10px;">
        ${
          this._variantValues.length === 0
            ? html`<div class="variant-empty">
                No values yet — add at least one (e.g. "Small").
              </div>`
            : this._variantValues.map(
                (v, i) => html`
                  <div class="value-row">
                    <sl-input
                      placeholder="Label (e.g. Small)"
                      .value=${v.label}
                      @sl-input=${(e: Event) => this._updateVariantValue(i, { label: (e.target as HTMLInputElement).value })}
                    ></sl-input>
                    <sl-input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Price"
                      .value=${v.price}
                      @sl-input=${(e: Event) => this._updateVariantValue(i, { price: (e.target as HTMLInputElement).value })}
                    ></sl-input>
                    <sl-input
                      placeholder="Barcode (optional)"
                      inputmode="numeric"
                      .value=${v.barcode}
                      @sl-input=${(e: Event) => this._updateVariantValue(i, { barcode: (e.target as HTMLInputElement).value })}
                    ></sl-input>
                    <sl-icon-button
                      name="trash"
                      label="Remove value"
                      @click=${() => this._removeVariantValue(i)}
                    ></sl-icon-button>
                  </div>
                `
              )
        }
      </div>
      <sl-button size="small" class="small-add" @click=${this._addVariantValue}>
        <sl-icon slot="prefix" name="plus-lg"></sl-icon>
        Add value
      </sl-button>
    `;
  }

  private _renderRecipe() {
    const comboMode = this._components.length > 0;
    const ingredientMode = this._ingredients.length > 0;
    return html`
      <p class="section-hint">
        Optional. Leave empty to sell on price alone. Add stock ingredients for
        a made-to-order item, or compose other products into a combo.
      </p>
      ${
        !comboMode
          ? html`
              <h3>Ingredients (stock)</h3>
              ${this._ingredients.map(
                (ing, i) => html`
                  <div class="ingredient-row">
                    <sl-select
                      .value=${ing.stockItemId}
                      @sl-change=${(e: Event) => this._updateIngredient(i, { stockItemId: (e.target as HTMLSelectElement).value })}
                      placeholder="Pick an ingredient"
                    >
                      ${this._allStock.map((s) => html`<sl-option value=${s.id}>${s.name} (${s.baseUnit})</sl-option>`)}
                    </sl-select>
                    <sl-input
                      type="number"
                      min="0"
                      step="any"
                      .value=${String(ing.quantity)}
                      @sl-input=${(e: Event) => this._updateIngredient(i, { quantity: parseFloat((e.target as HTMLInputElement).value) || 0 })}
                    ></sl-input>
                    <sl-icon-button
                      name="trash"
                      label="Remove"
                      @click=${() => this._removeIngredient(i)}
                    ></sl-icon-button>
                  </div>
                `
              )}
              <sl-button
                size="small"
                class="small-add"
                @click=${this._addIngredient}
                >Add ingredient</sl-button
              >
            `
          : nothing
      }
      ${
        !ingredientMode
          ? html`
              <h3 style="margin-top: 14px;">Combo items (products)</h3>
              ${this._components.map(
                (c, i) => html`
                  <div class="ingredient-row">
                    <sl-select
                      .value=${c.productId}
                      @sl-change=${(e: Event) => this._updateComponent(i, { productId: (e.target as HTMLSelectElement).value })}
                      placeholder="Pick a product"
                    >
                      ${this._allProducts
                        .filter((m) => m.id !== this._id)
                        .map(
                          (m) =>
                            html`<sl-option value=${m.id}>${m.name}</sl-option>`
                        )}
                    </sl-select>
                    <sl-input
                      type="number"
                      min="1"
                      step="1"
                      .value=${String(c.quantity)}
                      @sl-input=${(e: Event) => this._updateComponent(i, { quantity: parseInt((e.target as HTMLInputElement).value, 10) || 1 })}
                    ></sl-input>
                    <sl-icon-button
                      name="trash"
                      label="Remove"
                      @click=${() => this._removeComponent(i)}
                    ></sl-icon-button>
                  </div>
                `
              )}
              <sl-button
                size="small"
                class="small-add"
                @click=${this._addComponent}
                >Add combo item</sl-button
              >
            `
          : nothing
      }
    `;
  }

  private _renderOptionGroups() {
    return html`
      <h3>Modifier groups (size, sweetness, toppings, …)</h3>
      ${this._optionGroups.map(
        (g, gi) => html`
          <div class="group-card">
            <div class="row">
              <sl-input
                label="Group id (slug)"
                .value=${g.id}
                @sl-input=${(e: Event) => this._updateGroup(gi, { id: (e.target as HTMLInputElement).value.toLowerCase() })}
              ></sl-input>
              <sl-input
                label="Group name"
                .value=${g.name}
                @sl-input=${(e: Event) => this._updateGroup(gi, { name: (e.target as HTMLInputElement).value })}
              ></sl-input>
            </div>
            <div
              class="row"
              style="grid-template-columns: 1fr 1fr 40px; align-items: center; margin-top: 8px;"
            >
              <label style="display: flex; gap: 8px; align-items: center;">
                <sl-switch
                  ?checked=${g.required}
                  @sl-change=${(e: Event) => this._updateGroup(gi, { required: (e.target as HTMLInputElement).checked })}
                ></sl-switch>
                Required
              </label>
              <label style="display: flex; gap: 8px; align-items: center;">
                <sl-switch
                  ?checked=${g.multiSelect}
                  @sl-change=${(e: Event) => this._updateGroup(gi, { multiSelect: (e.target as HTMLInputElement).checked })}
                ></sl-switch>
                Multi-select
              </label>
              <sl-icon-button
                name="trash"
                label="Remove group"
                @click=${() => this._removeGroup(gi)}
              ></sl-icon-button>
            </div>
            <div style="margin-top: 8px;">
              <small style="opacity: 0.7;">Options</small>
              ${g.modifiers.map(
                (o, oi) => html`
                  <div class="option-row">
                    <sl-input
                      .value=${o.id}
                      placeholder="id (e.g. small)"
                      @sl-input=${(e: Event) => this._updateOption(gi, oi, { id: (e.target as HTMLInputElement).value.toLowerCase() })}
                    ></sl-input>
                    <sl-input
                      .value=${o.name}
                      placeholder="Name"
                      @sl-input=${(e: Event) => this._updateOption(gi, oi, { name: (e.target as HTMLInputElement).value })}
                    ></sl-input>
                    <sl-input
                      type="number"
                      step="any"
                      .value=${String(o.priceDelta)}
                      placeholder="Δ price"
                      @sl-input=${(e: Event) => this._updateOption(gi, oi, { priceDelta: parseFloat((e.target as HTMLInputElement).value) || 0 })}
                    ></sl-input>
                    <sl-icon-button
                      name="trash"
                      label="Remove option"
                      @click=${() => this._removeOption(gi, oi)}
                    ></sl-icon-button>
                  </div>
                  ${
                    g.multiSelect
                      ? html`
                          <div class="option-row option-row-quantifiable">
                            <label
                              style="display: flex; gap: 6px; align-items: center; grid-column: 1 / span 2;"
                            >
                              <sl-switch
                                ?checked=${o.quantifiable ?? false}
                                @sl-change=${(e: Event) => {
                                  const on = (e.target as HTMLInputElement)
                                    .checked;
                                  this._updateOption(gi, oi, {
                                    quantifiable: on,
                                    maxQuantity: on
                                      ? (o.maxQuantity ?? null)
                                      : null,
                                  });
                                }}
                              ></sl-switch>
                              <small
                                >Quantifiable (cashier can pick N of
                                this)</small
                              >
                            </label>
                            <sl-input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="max qty (blank = uncapped)"
                              ?disabled=${!(o.quantifiable ?? false)}
                              .value=${o.maxQuantity != null ? String(o.maxQuantity) : ''}
                              @sl-input=${(e: Event) => {
                                const raw = (
                                  e.target as HTMLInputElement
                                ).value.trim();
                                const parsed =
                                  raw === ''
                                    ? null
                                    : Math.max(1, Math.floor(Number(raw)));
                                this._updateOption(gi, oi, {
                                  maxQuantity: parsed,
                                });
                              }}
                            ></sl-input>
                          </div>
                        `
                      : nothing
                  }
                `
              )}
              <sl-button
                size="small"
                class="small-add"
                @click=${() => this._addOption(gi)}
                >Add option</sl-button
              >
            </div>
          </div>
        `
      )}
      <sl-button size="small" class="small-add" @click=${this._addGroup}
        >Add modifier group</sl-button
      >
    `;
  }

  render() {
    if (this._loading) return html`<div class="variant-empty">Loading…</div>`;

    const kind = this._hasVariants
      ? 'variant'
      : this._components.length > 0
        ? 'combo'
        : this._ingredients.length >= 1
          ? 'recipe'
          : 'flat';
    const kindClass =
      kind === 'combo' ? 'warn' : kind === 'variant' ? 'info' : 'good';
    const hasModifiers = this._optionGroups.length > 0;

    return html`
      <div class="page-head">
        <h1>${this._isEdit ? this._existing?.name : 'New product'}</h1>
        <span class="status-pill ${kindClass}"
          ><span class="dot"></span>${kind}</span
        >
      </div>

      <form @submit=${this._submit}>
        <!-- ===== Basics ===== -->
        <sl-details open>
          <span slot="summary" class="summary-line">Basics</span>
          <sl-input
            label="Id (slug)"
            help-text="Lowercase, hyphens. Cannot be changed later."
            .value=${this._id}
            ?disabled=${this._isEdit}
            @sl-input=${(e: Event) => {
              this._id = (e.target as HTMLInputElement).value.toLowerCase();
              this._saved = false;
            }}
            required
          ></sl-input>
          <sl-input
            label="Name"
            style="margin-top: 12px;"
            .value=${this._name}
            @sl-input=${(e: Event) => {
              this._name = (e.target as HTMLInputElement).value;
              this._saved = false;
            }}
            required
          ></sl-input>
          <sl-textarea
            label="Description"
            rows="2"
            style="margin-top: 12px;"
            .value=${this._description}
            @sl-input=${(e: Event) => {
              this._description = (e.target as HTMLInputElement).value;
              this._saved = false;
            }}
          ></sl-textarea>
          <div class="row" style="margin-top: 12px;">
            <sl-select
              label="Category"
              .value=${this._categoryId}
              @sl-change=${(e: Event) => {
                this._categoryId = (e.target as HTMLSelectElement).value;
                this._saved = false;
              }}
            >
              <sl-option value="">(none)</sl-option>
              ${this._categories.map((c) => html`<sl-option value=${c.id}>${c.name}</sl-option>`)}
            </sl-select>
            <sl-input
              label="Display order"
              type="number"
              .value=${this._displayOrder}
              @sl-input=${(e: Event) => {
                this._displayOrder = (e.target as HTMLInputElement).value;
                this._saved = false;
              }}
            ></sl-input>
          </div>
          ${
            this._isEdit
              ? html`
                  <label class="toggle-row" style="margin-top: 14px;">
                    <sl-switch
                      ?checked=${this._isActive}
                      @sl-change=${(e: Event) => {
                        this._isActive = (e.target as HTMLInputElement).checked;
                        this._saved = false;
                      }}
                    ></sl-switch>
                    <strong>Active</strong>
                  </label>
                `
              : nothing
          }
        </sl-details>

        <!-- ===== Variants toggle ===== -->
        <sl-details open>
          <span slot="summary" class="summary-line">
            Variants
            ${this._hasVariants ? html`<span class="count">${this._variantValues.length}</span>` : nothing}
          </span>
          <label
            class="toggle-row"
            style="align-items: flex-start; margin-bottom: 12px;"
          >
            <sl-switch
              ?checked=${this._hasVariants}
              @sl-change=${(e: Event) => {
                this._hasVariants = (e.target as HTMLInputElement).checked;
                this._saved = false;
              }}
            ></sl-switch>
            <span>
              <strong>This product comes in variants</strong><br />
              <small style="opacity: 0.7;"
                >Sizes, colours, pack sizes — each a separate priced, stockable
                item.</small
              >
            </span>
          </label>
          ${this._hasVariants ? this._renderVariantValues() : nothing}
        </sl-details>

        <!-- ===== Price + recipe (simple products only) ===== -->
        ${
          !this._hasVariants
            ? html`
                <sl-details open>
                  <span slot="summary" class="summary-line"
                    >Price &amp; recipe</span
                  >
                  <div class="row">
                    <sl-input
                      label="Price"
                      type="number"
                      min="0"
                      step="any"
                      .value=${this._price}
                      @sl-input=${(e: Event) => {
                        this._price = (e.target as HTMLInputElement).value;
                        this._saved = false;
                      }}
                      required
                    ></sl-input>
                    <sl-input
                      label="Barcode"
                      help-text="Scannable at the register (optional)."
                      inputmode="numeric"
                      .value=${this._barcode}
                      @sl-input=${(e: Event) => {
                        this._barcode = (e.target as HTMLInputElement).value;
                        this._saved = false;
                      }}
                    ></sl-input>
                  </div>
                  <div style="margin-top: 12px;">${this._renderRecipe()}</div>
                </sl-details>
              `
            : nothing
        }

        <!-- ===== Modifiers ===== -->
        <sl-details ?open=${hasModifiers}>
          <span slot="summary" class="summary-line">
            Modifiers
            ${this._optionGroups.length > 0 ? html`<span class="count">${this._optionGroups.length}</span>` : nothing}
          </span>
          ${this._renderOptionGroups()}
        </sl-details>

        ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}
        ${this._saved ? html`<sl-alert variant="success" open closable>Saved.</sl-alert>` : nothing}

        <page-action-bar>
          <sl-button slot="left" @click=${this._back} ?disabled=${this._busy}
            >Cancel</sl-button
          >
          <sl-button
            slot="right"
            type="submit"
            variant="primary"
            ?loading=${this._busy}
            ?disabled=${this._busy}
          >
            ${this._isEdit ? 'Save changes' : 'Create'}
          </sl-button>
        </page-action-bar>
      </form>

      ${
        this._isEdit && this._existing && this.activeShop
          ? html`
              <div style="margin-top: 1rem;">
                <product-image-editor
                  shop-name=${this.activeShop.shopName}
                  item-id=${this._existing.id}
                  current-url=${this._existing.imageUrl ?? ''}
                  @image-changed=${(e: CustomEvent) => {
                    if (this._existing) {
                      this._existing = {
                        ...this._existing,
                        imageUrl: e.detail.imageUrl,
                        hasImage: !!e.detail.imageUrl,
                      };
                    }
                  }}
                ></product-image-editor>
              </div>
            `
          : nothing
      }
      ${
        this._isEdit && this._existing?.isActive
          ? html`
              <div class="danger-zone">
                <strong>Deactivate</strong>
                <p style="margin: 4px 0 8px 0; opacity: 0.75;">
                  Hides this item from sales without losing history. You can
                  reactivate it later.
                </p>
                <sl-button
                  variant="warning"
                  @click=${this._deactivate}
                  ?disabled=${this._busy}
                >
                  Deactivate
                </sl-button>
              </div>
            `
          : nothing
      }
    `;
  }
}

function readEditIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!m) return null;
  if (m[1] === 'new' || m[1] === 'products' || m[1] === 'categories')
    return null;
  return decodeURIComponent(m[1]);
}
